import { deleteFromStorage, writeStorage } from '@rehooks/local-storage';

/**
 * Per-position booking verification token, stored in the browser so a customer
 * who verified once is not re-prompted on the same device. Scoped by queue and
 * position so one token can never be sent for another booking.
 *
 * Used by the axios interceptors and thunks in store/api.ts. Writes go through
 * the library's own helpers so any future `useLocalStorage` subscriber of the
 * same key is notified.
 */
export const BOOKING_VERIFICATION_TOKEN_HEADER = 'x-booking-verification-token';

export const bookingTokenKey = (queue: string, positionId: string) => `${queue}/booking-token/${positionId}`;

const read = (key: string): string | null => {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    // @rehooks/local-storage JSON-encodes values; tolerate both encodings
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : null;
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
};

export const getBookingToken = (queue: string, positionId: string): string | null =>
  read(bookingTokenKey(queue, positionId)) || null;

export const setBookingToken = (queue: string, positionId: string, token: string): void => {
  try {
    writeStorage(bookingTokenKey(queue, positionId), token);
  } catch {
    // storage unavailable (private mode, quota); the user will simply be re-prompted
  }
};

export const clearBookingToken = (queue: string, positionId: string): void => {
  try {
    deleteFromStorage(bookingTokenKey(queue, positionId));
  } catch {
    // ignore
  }
};

/**
 * Matches the URLs of every position-scoped call in store/api.ts and the
 * reschedule call (POST .../bookings with an id in the body), returning the
 * queue and position the request is about.
 */
export const resolveBookingRequestTarget = (
  url: string | undefined,
  method: string | undefined,
  body: unknown,
): { queue: string; positionId: string } | null => {
  if (!url) return null;
  const positionMatch = /organizations\/[^/]+\/queues\/([^/]+)\/positions\/([^/?]+)/.exec(url);
  if (positionMatch) {
    return { queue: positionMatch[1], positionId: positionMatch[2] };
  }

  const bookingsMatch = /organizations\/[^/]+\/queues\/([^/]+)\/bookings(?:[?#]|$)/.exec(url);
  if (bookingsMatch && (method ?? 'get').toLowerCase() === 'post') {
    const parsed = typeof body === 'string' ? safeParse(body) : body;
    const id = parsed && typeof parsed === 'object' ? (parsed as { id?: unknown }).id : undefined;
    if (typeof id === 'string' && id) {
      return { queue: bookingsMatch[1], positionId: id };
    }
  }

  return null;
};

const safeParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
